import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Capacitor } from '@capacitor/core';
import {
  CapacitorSQLite,
  SQLiteConnection,
  SQLiteDBConnection,
} from '@capacitor-community/sqlite';

const DB_NAME = 'offline_cache';
const DB_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class OfflineCacheService {
  private sqlite?: SQLiteConnection;
  private db?: SQLiteDBConnection;
  private initPromise?: Promise<SQLiteDBConnection | null>;

  constructor(private auth: Auth) {}

  async get<T>(key: string): Promise<T | null> {
    const db = await this.getDb();
    if (!db) return null;

    try {
      const res = await db.query(
        'SELECT value FROM cache_entries WHERE user_key = ? AND cache_key = ? LIMIT 1',
        [this.userKey(), key]
      );
      const raw = res.values?.[0]?.value;
      return raw ? JSON.parse(raw) as T : null;
    } catch {
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    const db = await this.getDb();
    if (!db) return;

    try {
      await db.run(
        `INSERT OR REPLACE INTO cache_entries (user_key, cache_key, saved_at, value)
         VALUES (?, ?, ?, ?)`,
        [this.userKey(), key, new Date().toISOString(), JSON.stringify(value)]
      );
    } catch {
      // Cache writes should never break the online request that produced the data.
    }
  }

  private async getDb(): Promise<SQLiteDBConnection | null> {
    if (!Capacitor.isNativePlatform()) return null;
    if (this.db) return this.db;
    if (!this.initPromise) this.initPromise = this.initDb();
    return this.initPromise;
  }

  private async initDb(): Promise<SQLiteDBConnection | null> {
    try {
      this.sqlite = new SQLiteConnection(CapacitorSQLite);
      await this.ensureEncryptionSecret();

      const db = await this.sqlite.createConnection(DB_NAME, true, 'secret', DB_VERSION, false);
      await db.open();
      await db.execute(`
        CREATE TABLE IF NOT EXISTS cache_entries (
          user_key TEXT NOT NULL,
          cache_key TEXT NOT NULL,
          saved_at TEXT NOT NULL,
          value TEXT NOT NULL,
          PRIMARY KEY (user_key, cache_key)
        );
        CREATE INDEX IF NOT EXISTS idx_cache_entries_saved_at
          ON cache_entries(saved_at);
      `);

      this.db = db;
      return db;
    } catch {
      return null;
    }
  }

  private async ensureEncryptionSecret(): Promise<void> {
    if (!this.sqlite) return;

    const stored = await this.sqlite.isSecretStored();
    if (stored.result) return;

    await this.sqlite.setEncryptionSecret(this.createEncryptionSecret());
  }

  private createEncryptionSecret(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private userKey(): string {
    return this.auth.currentUser?.uid ?? 'guest';
  }
}
